import React from 'react'
import QRCode from 'qrcode'

function ShareQrModal({ open, items, onClose, onCopy }) {
  const availableItems = React.useMemo(
    () => (Array.isArray(items) ? items.filter((item) => item?.url) : []),
    [items]
  )
  const [activeId, setActiveId] = React.useState('')
  const [qrDataUrl, setQrDataUrl] = React.useState('')

  React.useEffect(() => {
    if (!open) return
    setActiveId((prev) => {
      if (prev && availableItems.some((item) => item.id === prev)) return prev
      return availableItems[0]?.id || ''
    })
  }, [open, availableItems])

  const activeItem = React.useMemo(
    () => availableItems.find((item) => item.id === activeId) || availableItems[0] || null,
    [activeId, availableItems]
  )

  React.useEffect(() => {
    let cancelled = false
    async function generateQr() {
      if (!open || !activeItem?.url) {
        setQrDataUrl('')
        return
      }
      try {
        const next = await QRCode.toDataURL(activeItem.url, {
          width: 320,
          margin: 2,
          color: {
            dark: '#0f172a',
            light: '#ffffff',
          },
        })
        if (!cancelled) setQrDataUrl(next)
      } catch {
        if (!cancelled) setQrDataUrl('')
      }
    }
    generateQr()
    return () => { cancelled = true }
  }, [open, activeItem])

  const downloadQr = React.useCallback(() => {
    if (!qrDataUrl || !activeItem?.label) return
    const link = document.createElement('a')
    const safeName = String(activeItem.label).replace(/\s+/g, '-').replace(/[^\w-]/g, '').toLowerCase() || 'share-qr'
    link.href = qrDataUrl
    link.download = `${safeName}-qr.png`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }, [activeItem, qrDataUrl])

  if (!open) return null

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-slate-950/55 p-4">
      <div className="w-full max-w-xl rounded-3xl bg-white p-6 shadow-2xl">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="text-lg font-bold text-slate-900">공유용 QR</div>
            <div className="mt-1 text-sm text-slate-500">오프라인 홍보물이나 안내 메시지에 바로 쓸 수 있도록 QR 이미지를 생성했습니다.</div>
          </div>
          <button type="button" className="rounded-xl border border-slate-200 px-4 py-2 text-sm text-slate-700" onClick={onClose}>닫기</button>
        </div>

        <div className="mt-5 flex flex-wrap gap-2">
          {availableItems.map((item) => {
            const active = item.id === activeItem?.id
            return (
              <button
                key={item.id}
                type="button"
                className={`rounded-full px-4 py-2 text-sm font-semibold transition ${
                  active
                    ? 'bg-slate-900 text-white'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
                onClick={() => setActiveId(item.id)}
              >
                {item.label}
              </button>
            )
          })}
        </div>

        <div className="mt-5 rounded-3xl border border-slate-200 bg-slate-50 p-5">
          <div className="flex flex-col items-center gap-4 md:flex-row md:items-start">
            <div className="flex h-[220px] w-[220px] items-center justify-center rounded-3xl bg-white p-3 shadow-sm ring-1 ring-slate-200">
              {qrDataUrl ? (
                <img src={qrDataUrl} alt={`${activeItem?.label || '공유'} QR`} className="h-full w-full rounded-2xl object-contain" />
              ) : (
                <div className="text-sm text-slate-400">QR 생성 중...</div>
              )}
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-semibold text-slate-900">{activeItem?.label || '공유 링크'}</div>
              <div className="mt-2 break-all rounded-2xl bg-white px-4 py-3 text-xs leading-5 text-slate-500 ring-1 ring-slate-200">
                {activeItem?.url || '-'}
              </div>
              <div className="mt-4 flex flex-wrap gap-2">
                <button
                  type="button"
                  className="rounded-2xl bg-slate-900 px-4 py-2 text-sm font-semibold text-white"
                  onClick={() => activeItem?.url && onCopy?.(activeItem.url, `${activeItem.label}가 복사되었습니다.`)}
                >
                  링크 복사
                </button>
                <button
                  type="button"
                  className="rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700"
                  onClick={downloadQr}
                  disabled={!qrDataUrl}
                >
                  QR 이미지 다운로드
                </button>
              </div>
              <div className="mt-3 text-xs text-slate-400">
                카카오톡 공유용은 OG 미리보기가 적용되고, 공개 링크는 실제 페이지 주소를 그대로 전달할 때 적합합니다.
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default function ShareLinkActions({
  items = [],
  disabled = false,
  disabledMessage = '',
  onCopy,
  containerClassName = '',
  buttonClassName = '',
  showCopyButtons = true,
  showQrButton = true,
  qrButtonLabel = 'QR 보기',
}) {
  const [open, setOpen] = React.useState(false)
  const availableItems = items.filter((item) => item?.url)

  return (
    <>
      <div className={`flex flex-wrap items-center gap-2 ${containerClassName}`.trim()}>
        {showCopyButtons && items.map((item) => (
          <button
            key={item.id}
            type="button"
            className={`rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-700 disabled:cursor-not-allowed disabled:opacity-50 ${buttonClassName}`.trim()}
            disabled={disabled || !item?.url}
            title={disabled ? disabledMessage : `${item.label}를 복사합니다.`}
            onClick={() => item?.url && onCopy?.(item.url, `${item.label}가 복사되었습니다.`)}
          >
            {item.label} 복사
          </button>
        ))}
        {showQrButton ? (
          <button
            type="button"
            className={`rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-700 disabled:cursor-not-allowed disabled:opacity-50 ${buttonClassName}`.trim()}
            disabled={disabled || availableItems.length === 0}
            title={disabled ? disabledMessage : '공유용 QR을 확인합니다.'}
            onClick={() => setOpen(true)}
          >
            {qrButtonLabel}
          </button>
        ) : null}
      </div>
      <ShareQrModal
        open={open}
        items={availableItems}
        onClose={() => setOpen(false)}
        onCopy={onCopy}
      />
    </>
  )
}
